/**
 * Conditional checklist sections ("Verzweigungen"): a section row may carry
 * `config.visible_if = { question_id, equals }`. The section and every question up to the next
 * section are only shown when the referenced single-choice question (placed before the section)
 * is answered with `equals`. Mirrored in the field app (`lib/checklistVisibility.ts`).
 */
export type ChecklistVisibleIf = { question_id: string; equals: string };

export function parseChecklistVisibleIf(config: unknown): ChecklistVisibleIf | null {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
  const raw = (config as Record<string, unknown>).visible_if;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const questionId = String((raw as Record<string, unknown>).question_id ?? '').trim();
  const equals = String((raw as Record<string, unknown>).equals ?? '').trim();
  if (!questionId || !equals) return null;
  return { question_id: questionId, equals };
}

type VisibilityQuestion = { id: string; question_type: string; config?: unknown };

/**
 * Ids of questions hidden by section conditions, for questions in display order. A condition on a
 * question that is itself hidden never matches, so nested branches collapse together.
 */
export function hiddenChecklistQuestionIds(
  questions: readonly VisibilityQuestion[],
  singleChoiceAnswer: (questionId: string) => string | null,
): Set<string> {
  const hidden = new Set<string>();
  let sectionHidden = false;
  for (const q of questions) {
    if (q.question_type === 'section') {
      const condition = parseChecklistVisibleIf(q.config);
      sectionHidden = condition
        ? hidden.has(condition.question_id) || singleChoiceAnswer(condition.question_id) !== condition.equals
        : false;
    }
    if (sectionHidden) hidden.add(q.id);
  }
  return hidden;
}
